import { Box, Button, IconButton, LinearProgress, Slider, Typography } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isCaptureShortcut, paintBlackCanvas } from '../../utils/contentProtection';

const CAPTURE_LOCK_MS = 6000;

function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}

function drawWatermarks(ctx, width, height, label, offset) {
    ctx.save();
    ctx.globalAlpha = 0.38;
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px sans-serif';
    for (let i = 0; i < 6; i += 1) {
        const x = ((i * 17 + offset) % 80) / 100 * width;
        const y = (10 + i * 14) % 90 / 100 * height;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-0.45);
        ctx.fillText(label, 0, 0);
        ctx.restore();
    }
    ctx.restore();
}

/**
 * Canvas-only protected player. The <video> element is never visible in the DOM tree
 * (best-effort — browsers cannot block OS-level capture completely).
 */
export default function ProtectedVideoPlayer({
    src,
    watermark = 'Altus Academy',
    onEnded,
    onSecurityViolation,
    height = 360,
    labels = {},
}) {
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const savedTimeRef = useRef(0);
    const captureLockedRef = useRef(false);
    const captureLockedAtRef = useRef(0);
    const blockedRef = useRef(false);
    const blockReasonRef = useRef('focus');

    const [playing, setPlaying] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [blockReason, setBlockReason] = useState('focus');
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [watermarkOffset, setWatermarkOffset] = useState(0);
    const [resumeCooldown, setResumeCooldown] = useState(0);
    const [captureLocked, setCaptureLocked] = useState(false);

    const protectedTitle = labels.protectedTitle || 'Protected content';
    const protectedSubtitle = labels.protectedSubtitle
        || 'Playback paused — return to the page to continue.';
    const captureSubtitle = labels.captureSubtitle
        || 'Screen capture is not allowed on this page.';
    const resumeLabel = labels.resumeLabel || 'Resume playback';

    const syncBlocked = useCallback((value, reason = 'focus') => {
        blockedRef.current = value;
        blockReasonRef.current = reason;
        setBlocked(value);
        setBlockReason(reason);
    }, []);

    const paintBlackNow = useCallback(() => {
        paintBlackCanvas(canvasRef.current);
        const video = videoRef.current;
        if (video) {
            video.pause();
            video.muted = true;
        }
    }, []);

    const lockCapture = useCallback((reason = 'capture') => {
        captureLockedRef.current = true;
        setCaptureLocked(true);
        captureLockedAtRef.current = Date.now();
        paintBlackNow();
        syncBlocked(true, reason);
        setPlaying(false);

        const video = videoRef.current;
        if (video) {
            savedTimeRef.current = video.currentTime;
            video.pause();
            video.muted = true;
            video.removeAttribute('src');
            video.load();
        }

        onSecurityViolation?.();
    }, [onSecurityViolation, paintBlackNow, syncBlocked]);

    const pauseForFocusLoss = useCallback(() => {
        if (captureLockedRef.current) {
            lockCapture('capture');
            return;
        }
        const video = videoRef.current;
        if (video) {
            savedTimeRef.current = video.currentTime;
            video.pause();
            video.muted = true;
        }
        setPlaying(false);
        paintBlackNow();
        syncBlocked(true, 'focus');
    }, [lockCapture, paintBlackNow, syncBlocked]);

    const handleCaptureAttempt = useCallback((event) => {
        if (event?.preventDefault) event.preventDefault();
        paintBlackNow();
        lockCapture('capture');
        try {
            navigator.clipboard?.writeText('');
        } catch {
            /* ignore */
        }
    }, [lockCapture, paintBlackNow]);

    const canResume = useCallback(() => {
        return !document.hidden && document.hasFocus();
    }, []);

    const resumePlayback = useCallback(() => {
        if (!canResume()) return;
        if (captureLockedRef.current) {
            const elapsed = Date.now() - captureLockedAtRef.current;
            if (elapsed < CAPTURE_LOCK_MS) return;
        }

        const video = videoRef.current;
        if (!video || !src) return;

        captureLockedRef.current = false;
        setCaptureLocked(false);
        syncBlocked(false, 'focus');

        if (video.src !== src) {
            video.src = src;
            video.load();
        }
        video.muted = false;
        if (savedTimeRef.current > 0) {
            video.currentTime = savedTimeRef.current;
        }
    }, [canResume, src, syncBlocked]);

    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const width = container.clientWidth || 640;
        canvas.width = width;
        canvas.height = height;
        if (blockedRef.current || captureLockedRef.current) {
            paintBlackCanvas(canvas);
        }
    }, [height]);

    const renderFrame = useCallback(() => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        const mustHide = blockedRef.current
            || captureLockedRef.current
            || document.hidden
            || (!document.hasFocus() && playing);

        if (mustHide) {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else if (video.readyState >= 2 && !video.paused) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            drawWatermarks(
                ctx,
                canvas.width,
                canvas.height,
                `${watermark} • ${new Date().toLocaleDateString()}`,
                watermarkOffset
            );
        } else if (video.readyState >= 2) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            drawWatermarks(
                ctx,
                canvas.width,
                canvas.height,
                `${watermark} • ${new Date().toLocaleDateString()}`,
                watermarkOffset
            );
        } else {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        rafRef.current = requestAnimationFrame(renderFrame);
    }, [playing, watermark, watermarkOffset]);

    useEffect(() => {
        resizeCanvas();
        rafRef.current = requestAnimationFrame(renderFrame);
        window.addEventListener('resize', resizeCanvas);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [renderFrame, resizeCanvas]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                pauseForFocusLoss();
                return;
            }
            if (!captureLockedRef.current && blockReasonRef.current === 'focus' && canResume()) {
                syncBlocked(false, 'focus');
            }
        };
        const onBlur = () => pauseForFocusLoss();
        const onFocus = () => {
            if (captureLockedRef.current) {
                paintBlackNow();
                return;
            }
            if (blockReasonRef.current === 'focus' && canResume()) {
                syncBlocked(false, 'focus');
            }
        };
        const onPageHide = () => pauseForFocusLoss();
        const onBeforePrint = () => handleCaptureAttempt();
        const onCopy = (event) => {
            event.preventDefault();
            handleCaptureAttempt(event);
        };
        const onKeyDown = (event) => {
            if (isCaptureShortcut(event)) handleCaptureAttempt(event);
        };
        const onKeyUp = (event) => {
            if (isCaptureShortcut(event)) handleCaptureAttempt(event);
        };
        const onContextMenu = (event) => event.preventDefault();
        const onDragStart = (event) => event.preventDefault();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('beforeprint', onBeforePrint);
        document.addEventListener('copy', onCopy);
        document.addEventListener('cut', onCopy);
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('keyup', onKeyUp, true);
        document.addEventListener('contextmenu', onContextMenu, true);
        document.addEventListener('dragstart', onDragStart, true);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeprint', onBeforePrint);
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('cut', onCopy);
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('keyup', onKeyUp, true);
            document.removeEventListener('contextmenu', onContextMenu, true);
            document.removeEventListener('dragstart', onDragStart, true);
        };
    }, [canResume, handleCaptureAttempt, pauseForFocusLoss, syncBlocked]);

    useEffect(() => {
        if (!playing || blocked || captureLockedRef.current) return undefined;

        const intervalId = window.setInterval(() => {
            if (!document.hasFocus() || document.hidden) {
                pauseForFocusLoss();
            }
        }, 50);

        return () => window.clearInterval(intervalId);
    }, [playing, blocked, pauseForFocusLoss]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;
        const onEnterPiP = () => handleCaptureAttempt();
        const onLeavePiP = () => handleCaptureAttempt();
        video.addEventListener('enterpictureinpicture', onEnterPiP);
        video.addEventListener('leavepictureinpicture', onLeavePiP);
        return () => {
            video.removeEventListener('enterpictureinpicture', onEnterPiP);
            video.removeEventListener('leavepictureinpicture', onLeavePiP);
        };
    }, [handleCaptureAttempt, src]);

    useEffect(() => {
        const id = setInterval(() => setWatermarkOffset((value) => (value + 1) % 360), 3000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!blocked || !captureLocked) {
            setResumeCooldown(0);
            return undefined;
        }

        const tick = () => {
            const left = Math.max(0, CAPTURE_LOCK_MS - (Date.now() - captureLockedAtRef.current));
            setResumeCooldown(Math.ceil(left / 1000));
        };
        tick();
        const id = window.setInterval(tick, 250);
        return () => window.clearInterval(id);
    }, [blocked, blockReason, captureLocked]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        if (!captureLockedRef.current) {
            video.src = src;
            video.load();
        }

        setPlaying(false);
        setCurrent(0);
        setDuration(0);
        if (!captureLockedRef.current) {
            syncBlocked(false, 'focus');
        }
        savedTimeRef.current = 0;
    }, [src, syncBlocked]);

    const togglePlay = async () => {
        const video = videoRef.current;
        if (!video || blocked || captureLocked || !src) return;

        if (video.src !== src) {
            video.src = src;
            video.load();
        }

        if (video.paused) {
            video.muted = false;
            await video.play();
            setPlaying(true);
        } else {
            video.pause();
            setPlaying(false);
        }
    };

    const onTimeUpdate = () => {
        const video = videoRef.current;
        if (video) {
            setCurrent(video.currentTime);
            savedTimeRef.current = video.currentTime;
        }
    };

    const onLoaded = () => {
        const video = videoRef.current;
        if (video) setDuration(video.duration || 0);
    };

    const onSeek = (_, value) => {
        const video = videoRef.current;
        if (!video || blocked || captureLocked) return;
        video.currentTime = value;
        setCurrent(value);
        savedTimeRef.current = value;
    };

    if (!src) return null;

    const isCaptureBlock = blockReason === 'capture' || captureLocked;
    const overlayMessage = isCaptureBlock ? captureSubtitle : protectedSubtitle;
    const canPressResume = canResume() && (!captureLocked || resumeCooldown <= 0);

    return (
        <Box
            ref={containerRef}
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#000',
                mb: 2,
                position: 'relative',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                '@media print': { display: 'none' },
            }}
            onContextMenu={(event) => event.preventDefault()}
            onDragStart={(event) => event.preventDefault()}
        >
            <video
                ref={videoRef}
                style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: 'none',
                    left: -9999,
                    top: 0,
                }}
                playsInline
                disableRemotePlayback
                controls={false}
                controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
                onContextMenu={(event) => event.preventDefault()}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onLoaded}
                onEnded={() => {
                    setPlaying(false);
                    onEnded?.();
                }}
            />

            <canvas
                ref={canvasRef}
                style={{
                    width: '100%',
                    height,
                    display: 'block',
                    backgroundColor: '#000',
                    pointerEvents: 'none',
                }}
            />

            {blocked && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        minHeight: height,
                        bgcolor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        px: 2,
                        textAlign: 'center',
                        zIndex: 20,
                    }}
                >
                    <Typography variant="h6" color="#fff" fontWeight={700}>
                        {protectedTitle}
                    </Typography>
                    <Typography variant="body2" color="grey.500" sx={{ mt: 1, maxWidth: 420 }}>
                        {overlayMessage}
                    </Typography>
                    {isCaptureBlock && resumeCooldown > 0 ? (
                        <Typography variant="caption" color="grey.600" sx={{ mt: 1 }}>
                            {labels.cooldownLabel
                                ? labels.cooldownLabel.replace('{seconds}', String(resumeCooldown))
                                : `${resumeCooldown}s`}
                        </Typography>
                    ) : null}
                    <Button
                        variant="contained"
                        onClick={resumePlayback}
                        disabled={!canPressResume}
                        sx={{ mt: 2, bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
                    >
                        {resumeLabel}
                    </Button>
                </Box>
            )}

            <Box sx={{ px: 1.5, py: 1, bgcolor: '#000' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={togglePlay}
                        disabled={blocked || captureLocked}
                        sx={{ color: '#fff' }}
                    >
                        {playing ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: '#ccc', minWidth: 88 }}>
                        {formatTime(current)} / {formatTime(duration)}
                    </Typography>
                    <Slider
                        size="small"
                        min={0}
                        max={duration || 100}
                        value={current}
                        onChange={onSeek}
                        disabled={blocked || !duration || captureLocked}
                        sx={{ flex: 1, color: '#9c27b0' }}
                    />
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={duration ? (current / duration) * 100 : 0}
                    sx={{ mt: 0.5, opacity: 0.4, height: 2 }}
                />
            </Box>
        </Box>
    );
}
